import type { IconName } from '@getaccept/fried-tofu';
import { defaultDropdownOption } from '../constants/field-placeholders';
import type { UpdateDocumentPatch } from '../../types/update-document-patch';
import { AlignFieldType } from '../types/enums/align-field-type';
import type { FieldPicker } from '../types/field-picker';
import { EnumDocumentType } from '../../enums/DocumentType';
import { FieldSource } from '../types/enums/field-source';
import type { FieldPosition } from '../types/field-position';
import { MergeType } from '../types/enums/merge-type';
import type { Field } from '../types/field';
import { RecipientRole } from '../../recipients/enums/recipient-role';
import { FieldType } from '../types/enums/field-type';
import { t } from '../../helpers/translation.helper';
import type { DocumentPage } from '../../types/document-page';
import type { Document } from '../../types/Document';
import type { Entity } from '../../types/entity';
import { MergeFieldHelper } from '../../helpers/merge-field.helper';
import { MergeValue } from '../types/enums/merge-value';
import type { EventLog } from '../../timeline/types/event-log';
import { defaultSizes } from './field-default.helper';
import { FieldPlacementHelper } from './field-placement.helper';

export class FieldHelper {
  private static evaluateFieldForPresentation(picker: FieldPicker): boolean {
    switch (picker.type) {
      case FieldType.Signature:
      case FieldType.Initials:
        return true;
      case FieldType.Merge:
        return picker.mergeValue === MergeValue.SignDate;
      default:
        return false;
    }
  }

  private static evaluateFieldForRole(picker: FieldPicker): boolean {
    switch (picker.type) {
      case FieldType.Signature:
      case FieldType.Initials:
        return true;
      default:
        return false;
    }
  }

  static getCyclicIndex(index: number, arrayLength: number): number {
    return index % arrayLength;
  }

  static mapDataToField(
    field: Field,
    document: Document,
    entity: Entity,
    signedEvents: EventLog[]
  ) {
    switch (field.type) {
      case FieldType.Merge:
        return MergeFieldHelper.mapMergeField(field, document, entity, signedEvents);
      case FieldType.Text:
      case FieldType.Formatted:
        return MergeFieldHelper.mapTextField(field, document, entity);
      default:
        return field;
    }
  }
  static getPagesWithFields(pages: DocumentPage[], fields: Field[]): DocumentPage[] {
    return pages.map((page: DocumentPage) =>
      fields.some((field: Field) => field.pageId === page.id)
        ? { ...page, fields: fields.filter((field: Field) => field.pageId === page.id) }
        : page
    );
  }

  static getPageRatio(pageWidth: number): number {
    return pageWidth / 900;
  }

  static sortByPosition(fields: Field[], pages: DocumentPage[]): (Field & { pageNum: number })[] {
    if (!fields?.length || !pages?.length) {
      return [];
    }
    const fieldMap: { field: Field; pageNum: number }[] = fields.map((field: Field) => {
      const page = pages.find((documentPage: DocumentPage) => documentPage.id === field.pageId);
      if (!page) {
        return;
      }
      return { field, pageNum: page.pageNum };
    });
    const fieldMapSorted = fieldMap ? fieldMap.concat().sort(this.sortFieldComparison) : fieldMap;
    return fieldMapSorted.map(({ field, pageNum }: { field: Field; pageNum: number }) => ({
      ...field,
      pageNum,
    }));
  }

  private static sortFieldComparison(
    fieldMap1: { field: Field; pageNum: number },
    fieldMap2: { field: Field; pageNum: number }
  ): number {
    if (fieldMap1.pageNum - fieldMap2.pageNum !== 0) {
      return fieldMap1.pageNum - fieldMap2.pageNum;
    }
    if (fieldMap1.field.position.top < fieldMap2.field.position.top) {
      return -1;
    }
    if (
      fieldMap1.field.position.top === fieldMap2.field.position.top &&
      fieldMap1.field.position.left <= fieldMap2.field.position.left
    ) {
      return -1;
    }
    return 1;
  }

  static sortByPositionWithoutPages(originalFields: Field[]): Field[] {
    const fields = [...originalFields];
    return fields.sort((field1: Field, field2: Field) => {
      if (field1.position.top < field2.position.top) {
        return -1;
      }
      if (
        field1.position.top === field2.position.top &&
        field1.position.left <= field2.position.left
      ) {
        return -1;
      }
      return 1;
    });
  }

  static getNextTabField(fields: Field[], pages: DocumentPage[], tabStartField?: Field): Field {
    if (!tabStartField) {
      return fields[0];
    }
    const sortedFields: Field[] = this.sortByPosition(fields, pages);
    const index: number = sortedFields.findIndex((field: Field) => field.id === tabStartField.id);
    const nextIndex: number = this.getCyclicIndex(index + 1, fields.length);
    return sortedFields[nextIndex];
  }

  static getDuplicateFieldPosition(field: Field): FieldPosition {
    const space = 10;
    return {
      top: field.position.top + field.position.height + space,
      left: field.position.left + space,
      height: field.position.height,
      width: field.position.width,
    };
  }

  static getFieldContainerPositionBottom(fieldPosition: FieldPosition): number {
    return fieldPosition.top + fieldPosition.height;
  }

  static getFieldContainerPositionRight(fieldPosition: FieldPosition): number {
    return fieldPosition.left + fieldPosition.width;
  }

  static getAlignedFields(
    fields: Field[],
    actionType: AlignFieldType,
    fieldContainerPosition: FieldPosition
  ): Field[] {
    return fields.map((field: Field) => {
      switch (actionType) {
        case AlignFieldType.Top: {
          const { top } = fieldContainerPosition;
          if (field.position.top !== top) {
            field.position.top = top;
          }
          break;
        }
        case AlignFieldType.Right: {
          const right: number = this.getFieldContainerPositionRight(fieldContainerPosition);
          const left: number = right - field.position.width;
          if (field.position.left !== left) {
            field.position.left = left;
          }
          break;
        }
        case AlignFieldType.Left: {
          const { left } = fieldContainerPosition;
          if (field.position.left !== left) {
            field.position.left = left;
          }
          break;
        }
        case AlignFieldType.Bottom: {
          const bottom: number = this.getFieldContainerPositionBottom(fieldContainerPosition);
          if (field.position.top + field.position.height !== bottom) {
            field.position.top = bottom - field.position.height;
          }
          break;
        }
        case AlignFieldType.SameWidth: {
          if (field.type === FieldType.Check) {
            break;
          }
          const { width } = fields[0].position;
          if (field.position.width !== width) {
            field.position.width = width;
          }
          break;
        }
        default:
          break;
      }
      return field;
    });
  }

  static getTextSizes(): number[] {
    return [8, 9, 10, 11, 12, 13, 14, 18, 24, 30, 36, 48, 60, 72, 96];
  }

  static getDisplayFieldHeight(size: number): number {
    return Math.round(size * 0.6);
  }

  static getSaveFieldHeight(size: number): number {
    return Math.round(size / 0.6);
  }

  static getTextSize(fieldPosition: FieldPosition, pageWidth: number): number {
    const convertedFieldPosition: FieldPosition = FieldPlacementHelper.calculateFieldPositionForSave(
      fieldPosition,
      pageWidth
    );
    return this.getDisplayFieldHeight(convertedFieldPosition.height) || null;
  }

  static getRelativeFieldSize(
    fieldType: FieldType,
    pageWidth: number
  ): { width: number; height: number } {
    const size: {
      width: number;
      height: number;
    } = defaultSizes(fieldType);
    const ratio: number = this.getPageRatio(pageWidth);
    return {
      width: size.width * ratio,
      height: size.height * ratio,
    };
  }

  static getDragImageOffset(
    fieldType: FieldType,
    pageWidth: number
  ): { left: number; top: number } {
    const relativeSize: { width: number; height: number } = this.getRelativeFieldSize(
      fieldType,
      pageWidth
    );

    return {
      left: relativeSize.width / 2,
      top: relativeSize.height,
    };
  }

  static getSafeFieldLabel(field: Field): string {
    if (field.label) {
      return field.label;
    }
    let fieldLabel = 'field';
    switch (field.type) {
      case FieldType.Check:
        fieldLabel = 'checkbox-field';
        break;
      case FieldType.Dropdown:
        fieldLabel = 'dropdown-field';
        break;
      case FieldType.Formatted:
        fieldLabel = 'formatted-field';
        break;
      case FieldType.Initials:
        fieldLabel = 'initials-field';
        break;
      case FieldType.Link:
        fieldLabel = 'link-area';
        break;
      case FieldType.Merge:
        fieldLabel = 'merge-field';
        break;
      case FieldType.Signature:
        fieldLabel = 'signature-field';
        break;
      case FieldType.Text:
        fieldLabel = 'text-field';
        break;
    }
    return t(fieldLabel);
  }

  static disableField(
    documentType: EnumDocumentType,
    picker: FieldPicker,
    recipientRole: RecipientRole
  ): boolean {
    if (
      documentType === EnumDocumentType.Introduction ||
      documentType === EnumDocumentType.Presentation
    ) {
      return this.evaluateFieldForPresentation(picker);
    }
    if ([RecipientRole.CC, RecipientRole.Approver].includes(recipientRole)) {
      return this.evaluateFieldForRole(picker);
    }
    return false;
  }

  static getValidatedErrorMessage(type: FieldType): string {
    switch (type) {
      case FieldType.Formatted:
        return 'validation-is-required-for-this-field';
      case FieldType.Link:
        return 'please-enter-a-link';
      case FieldType.Dropdown:
        return 'a-minimum-of-one-option-is-required';
      default:
        return '';
    }
  }
  static getRequiredErrorMessage(type: FieldType): string {
    switch (type) {
      case FieldType.Dropdown:
        return 'you-need-to-select-an-option';
      case FieldType.Text:
      case FieldType.Formatted:
        return 'you-need-to-enter-text';
      default:
        return '';
    }
  }

  static getValidatedInput(field: Field): boolean {
    switch (field.type) {
      case FieldType.Formatted:
        return field.validationRegex ? true : false;
      case FieldType.Link:
        return field.value ? true : false;
      case FieldType.Dropdown:
        return field.options && field.options.length > 0 ? true : false;
      default: {
        return true;
      }
    }
  }

  static getSourceClass(source: FieldSource): string {
    switch (source) {
      case FieldSource.Sender:
        return 'sender-field';
      case FieldSource.Recipient:
        return 'recipient-field';
      case FieldSource.Document:
        return 'document-field';
      default:
        return '';
    }
  }

  static defaultHeight(fieldType: FieldType): number {
    return defaultSizes(fieldType).height;
  }

  static getTempId(): string {
    return Math.random().toString(36).substring(8);
  }

  static isCollectable = (
    recipientId: string,
    fieldType: FieldType,
    fieldValue: string,
    connectFirstRecipient = false
  ): boolean => {
    if (!recipientId && !connectFirstRecipient) {
      return false;
    }
    if (
      fieldType === FieldType.Merge &&
      (fieldValue === MergeType.SignDateStandard ||
        fieldValue === MergeType.SignDateShort ||
        fieldValue === MergeType.SignDateLong)
    ) {
      return true;
    }
    return [FieldType.Text, FieldType.Formatted, FieldType.Check, FieldType.Dropdown].includes(
      fieldType
    );
  };

  static getSignatureFields(fields: Field[]): Field[] {
    return fields.filter((field: Field) =>
      [FieldType.Initials, FieldType.Signature].includes(field.type)
    );
  }

  static getUpdatedDocumentBySignatureFields(fields: Field[]): UpdateDocumentPatch {
    const payload: UpdateDocumentPatch = {
      isSigning: 1,
    };
    if (fields.some((field: Field) => field.source === FieldSource.Sender)) {
      payload.isSelfsign = 1;
    }
    if (fields.some((field: Field) => field.type === FieldType.Signature)) {
      payload.isSigningBiometric = 1;
    }
    if (fields.some((field: Field) => field.type === FieldType.Initials)) {
      payload.isSigningInitials = 1;
    }
    return payload;
  }

  static getFieldIcon(fieldType: FieldType): IconName {
    switch (fieldType) {
      case FieldType.Check:
        return 'checkbox';
      case FieldType.Dropdown:
        return 'dropdown';
      case FieldType.Formatted:
        return 'text-check';
      case FieldType.Initials:
        return 'initials';
      case FieldType.Link:
        return 'link';
      case FieldType.Merge:
        return 'merge-tag';
      case FieldType.Signature:
        return 'signature';
      case FieldType.Text:
        return 'text';
    }
  }

  private static validateValue(validationRegex: string, value: string): boolean {
    return new RegExp(validationRegex).test(value);
  }

  static fieldCompleted(field: Field, verifiedInitialFieldIds?: string[]): boolean {
    switch (field.type) {
      case FieldType.Formatted:
        return !!field.value?.trim() && !!this.validateValue(field.validationRegex, field.value);
      case FieldType.Check:
        return field.value === '1';
      case FieldType.Initials:
        return verifiedInitialFieldIds?.includes(field.id) || false;
      case FieldType.Signature:
        return !!field.label;
      case FieldType.Dropdown:
        return !field.value || field.value === defaultDropdownOption ? false : true;
      case FieldType.Text:
        return !!field.value && !!field.value.trim();
      default:
        return !!field.value;
    }
  }

  static isInputField(fieldType: FieldType): boolean {
    switch (fieldType) {
      case FieldType.Check:
      case FieldType.Dropdown:
      case FieldType.Signature:
      case FieldType.Text:
      case FieldType.Formatted:
      case FieldType.Initials:
        return true;
      default:
        return false;
    }
  }

  static isTabbableField(fieldType: FieldType): boolean {
    switch (fieldType) {
      case FieldType.Check:
      case FieldType.Dropdown:
      case FieldType.Link:
      case FieldType.Signature:
      case FieldType.Text:
      case FieldType.Formatted:
      case FieldType.Initials:
        return true;
      default:
        return false;
    }
  }

  static showFieldTooltip(type: FieldType, disabled: boolean, requiredStepsLeft: boolean): boolean {
    if ([FieldType.Merge, FieldType.Link].includes(type)) {
      return false;
    }

    if (disabled) {
      return true;
    }

    if (type === FieldType.Signature) {
      return requiredStepsLeft;
    }

    return false;
  }
}
